import { FunctionComponent, useEffect, useState } from "react";
import { saveConsent, subscribeSettingsRequest } from "./consentStore";
import { consentTexts } from "./texts";
import { CookieDetails } from "./CookieDetails";
import { BackpackIcon } from "./BackpackIcon";
import { useConsent } from "./useConsent";
import "./consent.css";

type CookieBannerProps = {
  language?: string | null;
  privacyPolicyUrl?: string | null;
};

// Shown until the visitor decides. Afterwards a small cookie icon in the
// bottom-left corner (and the footer link) re-opens the settings.
export const CookieBanner: FunctionComponent<CookieBannerProps> = ({
  language,
  privacyPolicyUrl,
}) => {
  const t = consentTexts(language);
  const { consent, hasDecided } = useConsent();
  const [open, setOpen] = useState(!hasDecided);
  const [showSettings, setShowSettings] = useState(false);
  const [mapsChecked, setMapsChecked] = useState(
    consent?.externalMedia ?? false
  );

  useEffect(
    () =>
      subscribeSettingsRequest(() => {
        setMapsChecked(consent?.externalMedia ?? false);
        setShowSettings(true);
        setOpen(true);
      }),
    [consent]
  );

  if (!open) {
    if (!hasDecided) return null;
    return (
      <button
        type="button"
        className="swt-consent-icon"
        aria-label={t.footerLink}
        title={t.footerLink}
        onClick={() => {
          setMapsChecked(consent?.externalMedia ?? false);
          setShowSettings(true);
          setOpen(true);
        }}
      >
        <BackpackIcon size={22} />
      </button>
    );
  }

  const decide = (externalMedia: boolean) => {
    saveConsent({ externalMedia });
    setOpen(false);
    setShowSettings(false);
  };

  return (
    <div
      className="swt-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="swt-consent-title"
    >
      <div className="swt-consent__inner com-font">
        <div className="swt-consent__head">
          <span className="swt-consent__badge">
            <BackpackIcon size={20} />
          </span>
          <h2 id="swt-consent-title" className="swt-consent__title">
            {t.title}
          </h2>
        </div>
        <p className="swt-consent__text">
          {t.intro}{" "}
          {privacyPolicyUrl && (
            <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
              {t.privacyLink}
            </a>
          )}
        </p>

        {showSettings && (
          <div className="swt-consent__settings">
            <label className="swt-consent__option">
              <input type="checkbox" checked disabled />
              <span>
                <strong>{t.necessaryTitle}</strong> ({t.alwaysOn})
                <br />
                <small>{t.necessaryText}</small>
              </span>
            </label>
            <CookieDetails t={t} cookies={t.necessaryCookies} id="necessary" />
            <label className="swt-consent__option">
              <input
                type="checkbox"
                checked={mapsChecked}
                onChange={(e) => setMapsChecked(e.target.checked)}
                aria-label={t.mapsTitle}
              />
              <span>
                <strong>{t.mapsTitle}</strong>
                <br />
                <small>{t.mapsText}</small>
              </span>
            </label>
            <CookieDetails t={t} cookies={t.mapsCookies} id="maps" />
          </div>
        )}

        <div className="swt-consent__buttons">
          <button
            type="button"
            className="btn btn-sm swt-consent__btn"
            onClick={() => decide(true)}
          >
            {t.acceptAll}
          </button>
          <button
            type="button"
            className="btn btn-sm swt-consent__btn"
            onClick={() => decide(false)}
          >
            {t.necessaryOnly}
          </button>
          {showSettings ? (
            <button
              type="button"
              className="btn btn-sm swt-consent__btn--outline"
              onClick={() => decide(mapsChecked)}
            >
              {t.saveSelection}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm swt-consent__btn--outline"
              onClick={() => setShowSettings(true)}
            >
              {t.settings}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
